import { Box, Button, Typography } from "@mui/material";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";

import type { FallbackProps } from "react-error-boundary";

/**
 * Fallback component displayed when an error is caught
 * Matches the design: "An Unknown error has occurred" with reload button
 */
function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  // Log error to console for debugging (could also send to Sentry here)
  console.error("Application Error:", error);

  const handleReload = () => {
    // Try to reset the error boundary first
    resetErrorBoundary();
    // If that doesn't work, reload the page
    window.location.reload();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        bgcolor: "#ffffff",
        px: 3,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 600,
          color: "#1a1a1a",
          mb: 2,
        }}
      >
        An Unknown error has occurred
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "#666666",
          mb: 4,
          maxWidth: 400,
        }}
      >
        Something unexpected happened. Please try reloading the application. If
        this continues, contact the support desk.
      </Typography>

      <Button
        variant="contained"
        onClick={handleReload}
        sx={{
          backgroundColor: "#5b6abf",
          color: "#ffffff",
          textTransform: "uppercase",
          fontWeight: 500,
          px: 4,
          py: 1.5,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "#4a59a8",
          },
        }}
      >
        Reload Application
      </Button>

      {/* Show error details in development */}
      {import.meta.env.DEV && error && (
        <Box
          sx={{
            mt: 4,
            p: 2,
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            maxWidth: 600,
            width: "100%",
            textAlign: "left",
          }}
        >
          <Typography
            variant="caption"
            component="pre"
            sx={{
              color: "#d32f2f",
              fontFamily: "monospace",
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {error.message}
            {error.stack && `\n\n${error.stack}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * Error logging function - can be extended to send errors to Sentry or other services
 */
function logError(error: Error, info: { componentStack?: string | null }) {
  // Log to console
  console.error("Error caught by boundary:", error);
  if (info.componentStack) {
    console.error("Component stack:", info.componentStack);
  }

  // TODO: Send to Sentry if configured
  // Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Application Error Boundary
 * Wraps the entire application to catch and display errors gracefully
 */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={logError}
      onReset={() => {
        // Clear any cached state that might have caused the error
        // This runs when resetErrorBoundary is called
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export default ErrorBoundary;
